import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/config';

const MAPS_SCRIPT_ID = 'neoterra-google-maps-js';
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

let mapsPromise = null;

function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    window.gm_authFailure = () => reject(new Error('Google Maps auth failure'));

    const existing = document.getElementById(MAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps));
      existing.addEventListener('error', () =>
        reject(new Error('Google Maps failed to load'))
      );
      return;
    }

    const script = document.createElement('script');
    script.id = MAPS_SCRIPT_ID;
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('Google Maps failed to load'));
    document.head.appendChild(script);
  });

  return mapsPromise;
}

function formatDistance(meters) {
  if (typeof meters !== 'number') return '';
  return meters < 1000 ? `${meters} m` : `${(meters / 1000).toFixed(1)} km`;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function infoHtml({ title, subtitle, lines = [] }) {
  const extra = lines
    .filter(Boolean)
    .map(
      (line) =>
        `<div style="margin-top:6px;font-size:12px;color:#555;line-height:1.4">${escapeHtml(
          line
        )}</div>`
    )
    .join('');

  return `
    <div style="font-family:Poppins,sans-serif;max-width:260px;padding:2px 0">
      <div style="font-size:14px;font-weight:600;color:#0A0A0A">${escapeHtml(title)}</div>
      ${
        subtitle
          ? `<div style="margin-top:4px;font-size:12px;color:#BC8639">${escapeHtml(
              subtitle
            )}</div>`
          : ''
      }
      ${extra}
    </div>
  `;
}

const ProjectLocationMap = ({
  project,
  category,
  center,
  title,
  projectName,
  fallbackEmbedUrl,
  projectAreaLabel,
}) => {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const infoRef = useRef(null);
  const projectMarkerRef = useRef(null);
  const placeMarkersRef = useRef([]);
  const name = projectName || title || 'Project';

  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [useEmbed, setUseEmbed] = useState(!MAPS_KEY);

  useEffect(() => {
    if (!MAPS_KEY || !center?.lat || !center?.lng) {
      setUseEmbed(true);
      return undefined;
    }

    let cancelled = false;
    loadGoogleMaps(MAPS_KEY)
      .then((maps) => {
        if (cancelled || !mapNodeRef.current) return;

        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapNodeRef.current, {
            center,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          });
          infoRef.current = new maps.InfoWindow({ maxWidth: 280 });

          projectMarkerRef.current = new maps.Marker({
            map: mapRef.current,
            position: center,
            title: name,
            zIndex: 999,
            // Larger gold pin so project stays highlighted vs default red place pins
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#BC8639',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
            label: {
              text: name.charAt(0).toUpperCase(),
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: '700',
            },
          });

          projectMarkerRef.current.addListener('click', () => {
            infoRef.current.setContent(
              infoHtml({
                title: name,
                subtitle: 'Neoterra Project',
                lines: [projectAreaLabel],
              })
            );
            infoRef.current.open({
              map: mapRef.current,
              anchor: projectMarkerRef.current,
            });
          });

          infoRef.current.setContent(
            infoHtml({
              title: name,
              subtitle: 'Neoterra Project',
              lines: [projectAreaLabel],
            })
          );
          infoRef.current.open({
            map: mapRef.current,
            anchor: projectMarkerRef.current,
          });
        } else {
          mapRef.current.setCenter(center);
          projectMarkerRef.current?.setPosition(center);
        }

        setReady(true);
        setUseEmbed(false);
      })
      .catch(() => {
        if (!cancelled) setUseEmbed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [center, name, projectAreaLabel]);

  useEffect(() => {
    if (!project || !category) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    axiosInstance
      .get('/api/v1/places/nearby', { params: { project, category } })
      .then((res) => {
        if (!cancelled) setPlaces(res.data?.places || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setPlaces([]);
        setError(err?.response?.data?.message || 'Could not load nearby places');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [project, category]);

  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;

    placeMarkersRef.current.forEach((m) => m.setMap(null));
    placeMarkersRef.current = [];

    const maps = window.google.maps;
    const bounds = new maps.LatLngBounds();
    bounds.extend(center);

    places.forEach((place) => {
      if (typeof place.lat !== 'number' || typeof place.lng !== 'number') return;

      const marker = new maps.Marker({
        map: mapRef.current,
        position: { lat: place.lat, lng: place.lng },
        title: place.name,
      });

      marker.addListener('click', () => {
        const rating =
          typeof place.rating === 'number'
            ? `★ ${place.rating.toFixed(1)}${
                place.userRatingsTotal != null ? ` (${place.userRatingsTotal})` : ''
              }`
            : '';
        infoRef.current.setContent(
          infoHtml({
            title: place.name,
            subtitle: rating,
            lines: [
              place.vicinity,
              formatDistance(place.distanceMeters)
                ? `${formatDistance(place.distanceMeters)} away`
                : '',
            ],
          })
        );
        infoRef.current.open({ map: mapRef.current, anchor: marker });
      });

      placeMarkersRef.current.push(marker);
      bounds.extend({ lat: place.lat, lng: place.lng });
    });

    if (places.length > 0) {
      mapRef.current.fitBounds(bounds, 48);
    } else {
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(15);
    }
  }, [places, ready, center]);

  const embedSrc =
    fallbackEmbedUrl ||
    (projectAreaLabel
      ? `https://maps.google.com/maps?q=${encodeURIComponent(
          `${category || ''} near ${projectAreaLabel}`
        )}&hl=en&z=15&output=embed`
      : '');

  return (
    <div className="mt-[clamp(24px,3vh,40px)] w-full px-[5%]">
      <div className="relative mx-auto aspect-[350/500] w-full max-w-full overflow-hidden rounded-[7px] bg-[#f3f1ec] lg:aspect-[1296/500]">
        {useEmbed ? (
          <iframe
            title={title}
            src={embedSrc}
            className="absolute inset-0 h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        ) : (
          <div ref={mapNodeRef} className="absolute inset-0 h-full w-full" />
        )}

        {loading ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center">
            <span className="rounded bg-white/90 px-3 py-1 font-['Poppins',sans-serif] text-[12px] text-[#0A0A0A]">
              Loading nearby places…
            </span>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="mx-auto mt-3 font-['Poppins',sans-serif] text-[12px] text-[#b42318]">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default ProjectLocationMap;
